import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "cn";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Check,
  CircleCheck,
  FileText,
  Info,
  LayoutDashboard,
  ListChecks,
  MessageSquareWarning,
  Play,
  UserRound,
  X,
} from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { type ComponentType, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useIsMember } from "../../lib/permissions";
import type { IssueAttachment, IssueDetail, IssueTestStep, TestStepApp, TestStepOutcome } from "../../types";
import { Button, buttonVariants } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Kbd } from "../ui/kbd";
import { Textarea } from "../ui/textarea";
import { Markdown } from "./markdown";
import { CancelIssueButton, RequestChangesDialog, SignOffButton } from "./sign-off-actions";

type Outcome = { result: TestStepOutcome; note: string };
type AppInfo = { label: string; icon: ComponentType<{ className?: string }> };

const APPS: Record<TestStepApp, AppInfo> = {
  admin: { label: "Admin", icon: LayoutDashboard },
  client: { label: "Client portal", icon: UserRound },
  employer: { label: "Employer portal", icon: Building2 },
};
const ANY_APP: AppInfo = { label: "Steps", icon: ListChecks };

function appOf(step: IssueTestStep): AppInfo {
  return step.app ? APPS[step.app] : ANY_APP;
}

/** Open slide of the testing dialog: `intro`, a 1-based step number, or `summary`. */
function useTestingSlide() {
  return useQueryState("testing", parseAsString);
}

/** Why the signed-in person cannot record results on this issue, or null when they can. */
function useRecordBlock(issue: IssueDetail): string | null {
  const isMember = useIsMember();
  if (!isMember) return "Ask an admin for the user role to record results and sign off.";
  if (issue.statusKey !== "businessTesting") return "Results open once this issue is in Business Testing.";
  return null;
}

/** The signed-in person's progress through the steps, and what the main button does next. */
function testingProgress(issue: IssueDetail, canRecord: boolean) {
  const steps = issue.testSteps;
  const results = issue.testStepResults ?? {};
  const outcomes: Record<string, Outcome> = {};
  for (const step of steps) {
    const result = results[step.id];
    if (result) outcomes[step.id] = { result: result.result, note: result.note };
  }
  const checked = steps.filter((step) => outcomes[step.id]).length;
  const failed = steps.filter((step) => outcomes[step.id]?.result === "failed").length;
  const next = steps.findIndex((step) => !outcomes[step.id]);
  const action = !canRecord
    ? { label: "View test steps", slide: "intro" }
    : checked === 0
      ? { label: "Start testing", slide: "intro" }
      : next === -1
        ? { label: "Review results", slide: "summary" }
        : { label: `Resume at step ${next + 1}`, slide: String(next + 1) };
  return {
    outcomes,
    total: steps.length,
    checked,
    failed,
    allWorked: steps.length > 0 && checked === steps.length && failed === 0,
    action,
  };
}

/** The apps the steps visit, in the order a tester meets them. */
function appsLabel(steps: IssueTestStep[]): string {
  const labels = [...new Set(steps.flatMap((step) => (step.app ? [APPS[step.app].label] : [])))];
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/** Consecutive steps in the same app, so the rail groups them without reordering. */
function segments(steps: IssueTestStep[]) {
  const groups: Array<{ app: AppInfo; key: string; items: Array<{ step: IssueTestStep; index: number }> }> =
    [];
  steps.forEach((step, index) => {
    const last = groups.at(-1);
    if (last && last.key === (step.app ?? "any")) last.items.push({ step, index });
    else groups.push({ app: appOf(step), key: step.app ?? "any", items: [{ step, index }] });
  });
  return groups;
}

function progressText(checked: number, failed: number, total: number) {
  if (checked === 0) return `${total} ${total === 1 ? "step" : "steps"}, none checked yet`;
  if (checked === total && failed === 0) return total === 1 ? "The step worked" : `All ${total} steps worked`;
  const problems = failed === 0 ? "" : failed === 1 ? " · 1 problem" : ` · ${failed} problems`;
  return `${checked} of ${total} checked${problems}`;
}

function mediaKind(attachment: IssueAttachment): "image" | "video" | "file" {
  const type = attachment.contentType.toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  return "file";
}

function useAttachmentUrl(attachment: IssueAttachment | undefined) {
  return useQuery({
    queryKey: ["attachment-url", attachment?.id],
    queryFn: () => api.attachmentUrl(attachment!.id),
    staleTime: 10 * 60_000,
    enabled: Boolean(attachment),
  }).data?.url;
}

/**
 * Saves the signed-in person's outcome for a step, or clears it when the outcome is null,
 * showing it straight away and undoing it if the save fails.
 */
function useRecordOutcome(issue: IssueDetail, onChanged: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, outcome }: { stepId: string; outcome: Outcome | null }) => {
      if (!outcome) await api.clearTestStepResult(issue.identifier, stepId);
      else
        await api.setTestStepResult(issue.identifier, stepId, {
          result: outcome.result,
          ...(outcome.note ? { note: outcome.note } : {}),
        });
    },
    onMutate: async ({ stepId, outcome }) => {
      await queryClient.cancelQueries({ queryKey: ["issue"] });
      const previous = queryClient.getQueriesData<{ issue: IssueDetail }>({ queryKey: ["issue"] });
      queryClient.setQueriesData<{ issue: IssueDetail }>({ queryKey: ["issue"] }, (old) => {
        if (old?.issue?.id !== issue.id) return old;
        const { [stepId]: current, ...others } = old.issue.testStepResults ?? {};
        return {
          ...old,
          issue: {
            ...old.issue,
            testStepResults: outcome
              ? {
                  ...others,
                  [stepId]: {
                    stepId,
                    userId: current?.userId ?? "",
                    result: outcome.result,
                    note: outcome.note,
                    updatedAt: new Date().toISOString(),
                  },
                }
              : others,
          },
        };
      });
      return { previous };
    },
    onError: (error, { outcome }, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data);
      const fallback = outcome ? "Could not save that result" : "Could not clear that result";
      toast.error(error instanceof ApiError ? error.message : fallback);
    },
    onSettled: onChanged,
  });
}

function ProgressBar({
  steps,
  outcomes,
  current,
  className,
}: {
  steps: IssueTestStep[];
  outcomes: Record<string, Outcome>;
  current?: number | null;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-1", className)}>
      {steps.map((step, index) => (
        <span
          key={step.id}
          className={cn(
            "h-1.5 flex-1 rounded-full bg-muted",
            outcomes[step.id]?.result === "worked" && "bg-emerald-500",
            outcomes[step.id]?.result === "failed" && "bg-destructive",
            current === index && !outcomes[step.id] && "bg-primary",
          )}
        />
      ))}
    </div>
  );
}

function StepNumber({ n, outcome, current }: { n: number; outcome?: Outcome; current?: boolean }) {
  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums text-muted-foreground",
        current && "border-primary bg-primary text-primary-foreground",
        outcome?.result === "worked" &&
          "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        outcome?.result === "failed" && "border-destructive/40 bg-destructive/10 text-destructive",
      )}
    >
      {outcome?.result === "worked" ? (
        <Check className="size-3.5" />
      ) : outcome?.result === "failed" ? (
        <X className="size-3.5" />
      ) : (
        n
      )}
    </span>
  );
}

/**
 * The issue's test steps when the dialog is closed: a card that says how
 * many steps there are and how far the signed-in person got, and opens the
 * step-by-step dialog where they left off.
 */
export function TestStepsBlock({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const [slide, setSlide] = useTestingSlide();
  const recordBlock = useRecordBlock(issue);
  if (!issue.testSteps.length) return null;
  return (
    <>
      <DeckCard issue={issue} canRecord={!recordBlock} onOpen={(next) => void setSlide(next)} />
      <TestStepsDialog
        issue={issue}
        recordBlock={recordBlock}
        slide={slide}
        onSlideChange={(next) => void setSlide(next)}
        onChanged={onChanged}
      />
    </>
  );
}

function DeckCard({
  issue,
  canRecord,
  onOpen,
}: {
  issue: IssueDetail;
  canRecord: boolean;
  onOpen: (slide: string) => void;
}) {
  const { outcomes, checked, failed, total, allWorked, action } = testingProgress(issue, canRecord);
  const covers = issue.testSteps
    .flatMap((step) => step.attachments.filter((attachment) => mediaKind(attachment) === "image").slice(0, 1))
    .slice(0, 3);
  const evidence = issue.testSteps.filter((step) => step.attachments.length).length;
  const apps = appsLabel(issue.testSteps);
  return (
    <button
      type="button"
      onClick={() => onOpen(action.slide)}
      className="group/deck flex w-full flex-col items-stretch gap-4 rounded-xl border bg-card p-4 text-left transition-colors duration-(--motion-dur) hover:border-foreground/20 hover:bg-muted/30 sm:flex-row sm:items-center sm:gap-6 sm:pr-5"
    >
      <span className="relative ml-2 hidden h-24 w-40 shrink-0 sm:block">
        {covers.length ? (
          covers
            .slice()
            .reverse()
            .map((cover, i, list) => (
              <DeckCover key={cover.id} attachment={cover} depth={list.length - 1 - i} />
            ))
        ) : (
          <>
            <span className="absolute inset-0 -translate-x-2 -rotate-6 rounded-lg border bg-muted/60 transition-transform duration-(--motion-dur) group-hover/deck:-rotate-9" />
            <span className="absolute inset-0 translate-x-1.5 rotate-3 rounded-lg border bg-muted transition-transform duration-(--motion-dur) group-hover/deck:rotate-6" />
            <span className="absolute inset-0 flex items-center justify-center rounded-lg border bg-background shadow-sm">
              <ListChecks className="size-7 text-muted-foreground" />
            </span>
          </>
        )}
      </span>
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block text-[11px] font-semibold tracking-[0.07em] text-muted-foreground uppercase">
          Test steps
        </span>
        <span className="block text-base font-semibold tracking-tight">
          {checked === 0
            ? `${total} ${total === 1 ? "step" : "steps"} to check this change`
            : progressText(checked, failed, total)}
        </span>
        <span className="block text-sm text-muted-foreground">
          {apps ? `In ${apps}. ` : ""}Each step says what to do and what you should see
          {evidence === total ? ", with a screenshot." : evidence ? ", most with a screenshot." : "."}
        </span>
        {checked > 0 && (
          <ProgressBar steps={issue.testSteps} outcomes={outcomes} className="max-w-xs pt-1.5" />
        )}
      </span>
      <span
        className={cn(
          buttonVariants({ size: "lg", variant: allWorked ? "outline" : "default" }),
          "shrink-0",
          allWorked && "bg-background",
        )}
      >
        {canRecord && checked === 0 && <Play />}
        {action.label}
        <ArrowRight className="transition-transform duration-(--motion-dur) group-hover/deck:translate-x-0.5" />
      </span>
    </button>
  );
}

function DeckCover({ attachment, depth }: { attachment: IssueAttachment; depth: number }) {
  const url = useAttachmentUrl(attachment);
  const className = cn(
    "absolute inset-0 size-full rounded-lg border bg-muted object-cover object-top shadow-sm transition-transform duration-(--motion-dur)",
    depth === 2 && "-translate-x-2 -rotate-6 opacity-60 group-hover/deck:-rotate-9",
    depth === 1 && "translate-x-1.5 rotate-3 opacity-80 group-hover/deck:rotate-6",
  );
  return url ? <img src={url} alt="" className={className} /> : <span className={className} />;
}

/** Test steps inside the Business sign-off card: test first, then sign off. */
export function SignOffTesting({ issue }: { issue: IssueDetail }) {
  const canRecord = !useRecordBlock(issue);
  const [, setSlide] = useTestingSlide();
  const { outcomes, checked, failed, total, allWorked, action } = testingProgress(issue, canRecord);
  if (!total) return null;
  return (
    <div className="space-y-2.5 border-t px-4 py-3">
      <div className="flex items-center gap-2 text-sm">
        <ListChecks className="size-4 text-muted-foreground" />
        <span className="font-medium">
          {total} test {total === 1 ? "step" : "steps"}
        </span>
        <span className="ml-auto truncate text-xs text-muted-foreground tabular-nums">
          {checked === 0 ? appsLabel(issue.testSteps) : progressText(checked, failed, total)}
        </span>
      </div>
      <ProgressBar steps={issue.testSteps} outcomes={outcomes} />
      <Button
        className="w-full"
        variant={canRecord && !allWorked ? "default" : "outline"}
        onClick={() => void setSlide(action.slide)}
      >
        {canRecord && checked === 0 && <Play />}
        {action.label}
      </Button>
    </div>
  );
}

/** Whether every step worked for the signed-in person. Null when the issue has no steps. */
export function useStepsAllWorked(issue: IssueDetail): boolean | null {
  const canRecord = !useRecordBlock(issue);
  if (!issue.testSteps.length) return null;
  return testingProgress(issue, canRecord).allWorked;
}

function TestStepsDialog({
  issue,
  recordBlock,
  slide,
  onSlideChange,
  onChanged,
}: {
  issue: IssueDetail;
  recordBlock: string | null;
  slide: string | null;
  onSlideChange: (slide: string | null) => void;
  onChanged: () => void;
}) {
  const canRecord = !recordBlock;
  const steps = issue.testSteps;
  const total = steps.length;
  const { outcomes, checked } = testingProgress(issue, canRecord);
  const record = useRecordOutcome(issue, onChanged);
  const order = useMemo(() => ["intro", ...steps.map((_, i) => String(i + 1)), "summary"], [steps]);
  const position = slide ? order.indexOf(slide) : -1;
  const open = position >= 0;
  const stepIndex = position >= 1 && position <= total ? position - 1 : null;
  const step = stepIndex === null ? null : steps[stepIndex];
  const panelRef = useRef<HTMLDivElement>(null);

  const go = (delta: number) => {
    const next = order[Math.min(order.length - 1, Math.max(0, position + delta))];
    if (next) onSlideChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onSlideChange(null)}>
      <DialogContent
        sheet={false}
        className="flex h-[min(42rem,calc(100dvh-2rem))] w-[calc(100%-1rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:top-8 sm:bottom-8 sm:h-auto sm:w-[min(calc(var(--container-9xl)+6rem),calc(100%-2rem))] sm:max-w-none sm:translate-y-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panelRef.current?.focus();
        }}
        onKeyDown={(event) => {
          if (event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement) return;
          if (event.key === "ArrowRight") go(1);
          if (event.key === "ArrowLeft") go(-1);
        }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b px-5 py-3 pr-12">
          <ListChecks className="size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm font-semibold">Testing {issue.identifier}</DialogTitle>
            <DialogDescription className="truncate text-xs">{issue.title}</DialogDescription>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {canRecord && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {checked} of {total} checked
              </span>
            )}
            <ProgressBar
              steps={steps}
              outcomes={outcomes}
              current={stepIndex}
              className="hidden w-40 sm:flex"
            />
          </div>
        </div>

        <div className="grid min-h-0 flex-1 md:grid-cols-[15rem_minmax(0,1fr)]">
          <nav
            aria-label="Steps"
            className="hidden min-h-0 flex-col gap-3 overflow-y-auto border-r bg-muted/30 p-3 md:flex"
          >
            <RailItem active={slide === "intro"} onClick={() => onSlideChange("intro")}>
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-muted-foreground">
                <Info className="size-3.5" />
              </span>
              <span className="text-sm">Before you start</span>
            </RailItem>
            {segments(steps).map(({ app, items }) => (
              <div key={items[0]!.step.id} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5 px-2 pb-1 text-[11px] font-semibold tracking-[0.07em] text-muted-foreground uppercase">
                  <app.icon className="size-3" /> {app.label}
                </div>
                {items.map(({ step: item, index }) => (
                  <RailItem
                    key={item.id}
                    active={stepIndex === index}
                    onClick={() => onSlideChange(String(index + 1))}
                  >
                    <StepNumber n={index + 1} outcome={outcomes[item.id]} current={stepIndex === index} />
                    <span className="line-clamp-2 text-sm leading-snug">{item.title}</span>
                  </RailItem>
                ))}
              </div>
            ))}
            <RailItem active={slide === "summary"} onClick={() => onSlideChange("summary")}>
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-muted-foreground">
                <CircleCheck className="size-3.5" />
              </span>
              <span className="text-sm">{canRecord ? "Summary and sign-off" : "Summary"}</span>
            </RailItem>
          </nav>

          <div ref={panelRef} tabIndex={-1} className="min-h-0 overflow-y-auto outline-none">
            {slide === "intro" && (
              <IntroSlide issue={issue} recordBlock={recordBlock} onStart={() => go(1)} />
            )}
            {step && stepIndex !== null && (
              <StepSlide
                key={step.id}
                step={step}
                index={stepIndex}
                total={total}
                canRecord={canRecord}
                outcome={outcomes[step.id]}
                onRecord={(outcome) => {
                  const here = String(stepIndex + 1);
                  record.mutate({ stepId: step.id, outcome }, { onError: () => onSlideChange(here) });
                  go(1);
                }}
                onClear={() => record.mutate({ stepId: step.id, outcome: null })}
              />
            )}
            {slide === "summary" && (
              <SummarySlide
                issue={issue}
                recordBlock={recordBlock}
                outcomes={outcomes}
                onOpenStep={onSlideChange}
                onFinished={() => {
                  onSlideChange(null);
                  onChanged();
                }}
              />
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 border-t bg-muted/30 px-5 py-3">
          <Button variant="outline" disabled={position <= 0} onClick={() => go(-1)}>
            <ArrowLeft /> Previous
          </Button>
          <span className="mx-auto hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
            <Kbd>←</Kbd>
            <Kbd>→</Kbd> to move between steps
          </span>
          {slide !== "summary" && (
            <Button variant="outline" className="ml-auto sm:ml-0" onClick={() => go(1)}>
              {slide === "intro" ? "Start" : stepIndex === total - 1 ? "Summary" : "Next"} <ArrowRight />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RailItem({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "step" : undefined}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors duration-(--motion-dur) hover:bg-muted",
        active && "bg-background ring-1 ring-foreground/10 hover:bg-background",
      )}
    >
      {children}
    </button>
  );
}

function IntroSlide({
  issue,
  recordBlock,
  onStart,
}: {
  issue: IssueDetail;
  recordBlock: string | null;
  onStart: () => void;
}) {
  const steps = issue.testSteps;
  const apps = appsLabel(steps);
  const setup = issue.testingInstructions.trim();
  const appGroups = (["admin", "client", "employer"] as const).flatMap((app) => {
    const count = steps.filter((step) => step.app === app).length;
    return count ? [{ app, count }] : [];
  });
  const preview = issue.preview?.status === "ready" ? issue.preview : null;
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-7">
      <div className="space-y-2">
        <p className="text-[11px] font-semibold tracking-[0.07em] text-muted-foreground uppercase">
          Before you start
        </p>
        <h2 className="text-xl font-semibold tracking-tight">Check the change step by step</h2>
        <p className="text-sm text-muted-foreground">
          {steps.length} short {steps.length === 1 ? "step" : "steps"}
          {apps ? ` in ${apps}` : ""}. Each one says what to do and what you should see.{" "}
          {recordBlock ?? "Mark each step as you go, then sign off at the end."}
        </p>
      </div>
      {setup && <Markdown className="[&_strong]:font-medium">{setup}</Markdown>}
      {appGroups.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {appGroups.map(({ app, count }) => {
            const App = APPS[app];
            const url = preview?.apps?.[app] ?? null;
            const body = (
              <>
                <span className="flex size-8 items-center justify-center rounded-md bg-muted">
                  <App.icon className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{App.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {count === 1 ? "1 step" : `${count} steps`} ·{" "}
                    {url ? "open the preview in a new tab" : "no preview running"}
                  </span>
                </span>
              </>
            );
            return url ? (
              <a
                key={app}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="group/app flex items-center gap-3 rounded-lg border bg-card px-3 py-3 transition-colors hover:border-foreground/20"
              >
                {body}
                <ArrowUpRight className="size-4 text-muted-foreground group-hover/app:text-foreground" />
              </a>
            ) : (
              <div key={app} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-3">
                {body}
              </div>
            );
          })}
        </div>
      )}
      <div>
        <Button size="lg" onClick={onStart}>
          Start with step 1 <ArrowRight />
        </Button>
      </div>
    </div>
  );
}

function StepSlide({
  step,
  index,
  total,
  canRecord,
  outcome,
  onRecord,
  onClear,
}: {
  step: IssueTestStep;
  index: number;
  total: number;
  canRecord: boolean;
  outcome?: Outcome;
  onRecord: (outcome: Outcome) => void;
  onClear: () => void;
}) {
  const App = appOf(step);
  const [active, setActive] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [note, setNote] = useState(outcome?.result === "failed" ? outcome.note : "");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const current = step.attachments[Math.min(active, step.attachments.length - 1)];

  useEffect(() => {
    if (reporting) noteRef.current?.focus();
  }, [reporting]);

  return (
    <div className="grid gap-6 px-6 py-6 lg:h-full lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-8">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {step.app && (
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium text-foreground">
              <App.icon className="size-3" /> {App.label}
            </span>
          )}
          <span className="tabular-nums">
            Step {index + 1} of {total}
          </span>
        </div>
        <h2 className="text-xl font-semibold tracking-tight">{step.title}</h2>
        {step.description && <Markdown className="[&_strong]:font-medium">{step.description}</Markdown>}
        {step.expected && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              <CircleCheck className="size-3.5" /> You should see
            </div>
            <Markdown className="[&_strong]:font-medium">{step.expected}</Markdown>
          </div>
        )}

        {canRecord && (
          <div className="mt-2 flex flex-col gap-2 border-t pt-4">
            <div className="flex h-4 items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground">Did it match?</p>
              {outcome && !reporting && (
                <Button
                  variant="link"
                  size="xs"
                  title="Mark this step as not checked"
                  className="h-auto p-0 font-normal text-muted-foreground/70 hover:text-foreground"
                  onClick={() => {
                    setNote("");
                    onClear();
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
            {reporting ? (
              <form
                className="flex flex-col gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (note.trim()) onRecord({ result: "failed", note: note.trim() });
                }}
              >
                <Textarea
                  ref={noteRef}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  aria-label="What did you see instead?"
                  placeholder="What did you see instead? For example: no error appeared and the total dropped to $1,521.00."
                />
                <div className="flex gap-2">
                  <Button type="submit" variant="destructive" disabled={!note.trim()}>
                    <MessageSquareWarning /> Save and continue
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setReporting(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <>
                {outcome?.result === "failed" && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                    <span className="font-medium text-destructive">You saw: </span>
                    {outcome.note}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    className={cn(
                      "flex-1",
                      outcome?.result === "worked" && "bg-emerald-600 text-white hover:bg-emerald-600/90",
                    )}
                    onClick={() => onRecord({ result: "worked", note: "" })}
                  >
                    <Check /> {outcome?.result === "worked" ? "Worked" : "Yes, it worked"}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setReporting(true)}>
                    <X /> {outcome?.result === "failed" ? "Edit what went wrong" : "No, something's wrong"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-0 min-w-0 flex-col gap-2">
        {current ? (
          <>
            <StepMedia attachment={current} />
            <p className="text-xs text-muted-foreground">
              {current.caption ? `${current.caption} · ` : ""}
              <span className="font-mono">{current.fileName}</span>
            </p>
            {step.attachments.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {step.attachments.map((item, i) => (
                  <StepThumbnail
                    key={item.id}
                    attachment={item}
                    active={i === active}
                    onSelect={() => setActive(i)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex aspect-[16/10] flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-center lg:aspect-auto lg:flex-1">
            <p className="text-sm font-medium">No screenshot for this step</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Follow the instructions and compare what you see with the expected result.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepMedia({ attachment }: { attachment: IssueAttachment }) {
  const url = useAttachmentUrl(attachment);
  const kind = mediaKind(attachment);
  const frame = "block self-start overflow-hidden rounded-lg border bg-muted";
  if (!url) return <div className={cn(frame, "aspect-[16/10] w-full max-w-2xl animate-pulse")} />;
  if (kind === "video") {
    return (
      // biome-ignore lint/a11y/useMediaCaption: agent screen recordings have no caption track
      <video
        src={url}
        controls
        preload="metadata"
        className={cn(frame, "max-h-[calc(100dvh-21rem)] w-auto max-w-full")}
      />
    );
  }
  if (kind === "image") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={frame}>
        <img
          src={url}
          alt={attachment.caption ?? attachment.fileName}
          className="block max-h-[calc(100dvh-21rem)] w-auto max-w-full object-contain"
        />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 self-start rounded-lg border bg-card px-3 py-3 text-sm transition-colors hover:border-foreground/20"
    >
      <FileText className="size-5 text-muted-foreground" />
      Open {attachment.fileName}
      <ArrowUpRight className="size-4 text-muted-foreground" />
    </a>
  );
}

function StepThumbnail({
  attachment,
  active,
  onSelect,
}: {
  attachment: IssueAttachment;
  active: boolean;
  onSelect: () => void;
}) {
  const url = useAttachmentUrl(attachment);
  return (
    <button
      type="button"
      aria-label={attachment.caption ?? attachment.fileName}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
      className={cn(
        "flex h-14 w-22 items-center justify-center overflow-hidden rounded-md border bg-muted",
        active ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100",
      )}
    >
      {url && mediaKind(attachment) === "image" ? (
        <img src={url} alt="" className="size-full object-cover object-top" />
      ) : (
        <FileText className="size-4 text-muted-foreground" />
      )}
    </button>
  );
}

/** The feedback Request changes sends, one paragraph per step that went wrong. */
function draftFeedback(failed: Array<{ step: IssueTestStep; index: number; note: string }>): string {
  return failed
    .map(({ step, index, note }) =>
      [
        `Step ${index + 1}${step.app ? `, ${APPS[step.app].label}` : ""}: ${step.title}`,
        step.expected ? `- Expected: ${step.expected.replace(/\s+/g, " ").trim()}` : null,
        `- Saw: ${note.replace(/\s+/g, " ").trim()}`,
      ]
        .filter(Boolean)
        .join("\n"),
    )
    .join("\n\n");
}

function SummarySlide({
  issue,
  recordBlock,
  outcomes,
  onOpenStep,
  onFinished,
}: {
  issue: IssueDetail;
  recordBlock: string | null;
  outcomes: Record<string, Outcome>;
  onOpenStep: (slide: string) => void;
  onFinished: () => void;
}) {
  const steps = issue.testSteps;
  const worked = steps.filter((step) => outcomes[step.id]?.result === "worked").length;
  const failed = steps.flatMap((step, index) => {
    const outcome = outcomes[step.id];
    return outcome?.result === "failed" ? [{ step, index, note: outcome.note }] : [];
  });
  const unchecked = steps.filter((step) => !outcomes[step.id]).length;
  const canRecord = !recordBlock;
  const signOff = issue.signOff;
  const canAct = canRecord && Boolean(signOff?.canAct);
  const blocked =
    recordBlock ?? (signOff?.approvedBy ? `Signed off by ${signOff.approvedBy}.` : (signOff?.reason ?? null));

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-7">
      <div className="space-y-2">
        <p className="text-[11px] font-semibold tracking-[0.07em] text-muted-foreground uppercase">Summary</p>
        <h2 className="text-xl font-semibold tracking-tight">
          {!canRecord
            ? `${steps.length} test ${steps.length === 1 ? "step" : "steps"}`
            : unchecked === steps.length
              ? "No steps checked yet"
              : `${worked} of ${steps.length} steps worked`}
        </h2>
        {canRecord && (
          <p className="text-sm text-muted-foreground">
            {failed.length
              ? "Send what went wrong to the Fixer. The fix comes back here for another round of testing."
              : unchecked
                ? `${unchecked === 1 ? "1 step has" : `${unchecked} steps have`} not been checked yet.`
                : "Everything matched. Sign off to merge the change; it ships with the next release."}
          </p>
        )}
      </div>
      <ol className="divide-y overflow-hidden rounded-lg border bg-card">
        {steps.map((step, index) => {
          const outcome = outcomes[step.id];
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onOpenStep(String(index + 1))}
                className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted/50"
              >
                <StepNumber n={index + 1} outcome={outcome} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{step.title}</span>
                  {outcome?.note ? (
                    <span className="block text-xs text-destructive">{outcome.note}</span>
                  ) : canRecord && !outcome ? (
                    <span className="block text-xs text-muted-foreground">Not checked</span>
                  ) : null}
                </span>
                {step.app && <span className="text-xs text-muted-foreground">{APPS[step.app].label}</span>}
              </button>
            </li>
          );
        })}
      </ol>
      {canRecord && failed.length > 0 ? (
        <div className="rounded-lg border bg-muted/30 p-3">
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Request changes will send</p>
          <Markdown className="text-sm">{draftFeedback(failed)}</Markdown>
          <div className="mt-3 flex items-center justify-end gap-3">
            {!canAct && blocked && <p className="mr-auto text-xs text-muted-foreground">{blocked}</p>}
            {canAct && <CancelIssueButton issue={issue} size="default" onCancelled={onFinished} />}
            <RequestChangesDialog
              issue={issue}
              size="default"
              variant="default"
              disabled={!canAct}
              defaultFeedback={draftFeedback(failed)}
              onSent={onFinished}
            >
              <MessageSquareWarning /> Request changes
            </RequestChangesDialog>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          {canRecord && (
            <div className="flex flex-wrap items-center gap-2">
              <SignOffButton
                issue={issue}
                size="lg"
                disabled={!canAct || unchecked > 0}
                onSigned={onFinished}
              >
                <CircleCheck /> Sign off and merge
              </SignOffButton>
              {canAct && <CancelIssueButton issue={issue} size="lg" onCancelled={onFinished} />}
            </div>
          )}
          {!canAct && blocked && <p className="text-xs text-muted-foreground">{blocked}</p>}
        </div>
      )}
    </div>
  );
}
